import { NavigationError, provideRouter, withNavigationErrorHandler } from '@angular/router';
import { appRoutes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgxMaskConfig, provideEnvironmentNgxMask } from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import { importProvidersFrom, LOCALE_ID } from '@angular/core';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { ru, kk, enGB } from 'date-fns/locale';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { GtagModule } from 'angular-gtag';
import { DateInterceptor } from '@auth/interceptors/date-interceptor';
import { RefreshTokenInterceptor } from '@auth/interceptors/token-interceptor';
import { registerLocaleData } from '@angular/common';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import localeRu from '@angular/common/locales/ru';
import localeKk from '@angular/common/locales/kk';
import localeEn from '@angular/common/locales/en';
import { ApplicationConfig } from '@angular/core';
import { provideHighcharts } from 'highcharts-angular';

registerLocaleData(localeRu, 'ru');
registerLocaleData(localeKk, 'kk');
registerLocaleData(localeEn, 'en');
// Date-fns locales are loaded on-demand, no need to import them explicitly

const maskConfig: Partial<NgxMaskConfig> = {
  validation: false,
};

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export const MY_FORMATS = {
  parse: {
    dateInput: 'dd/MM/yyyy',
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'dd/MM/yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      withNavigationErrorHandler((e: NavigationError) => {
        const raw = e.error;
        const msg = typeof raw === 'string' ? raw : typeof raw?.message === 'string' ? raw.message : String(raw ?? '');

        const isChunkError =
          msg.includes('Failed to fetch dynamically imported module') ||
          msg.includes('Loading chunk') ||
          msg.includes('ChunkLoadError') ||
          msg.includes('Failed to load module script');

        if (!isChunkError) return;
        if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
        if (!navigator.onLine) {
          console.warn('[ChunkReload] offline, skipping reload');
          return;
        }

        const RELOAD_KEY = 'chunk_reload_timestamp';
        const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || '0');
        if (Date.now() - lastReload < 10_000) {
          console.warn('[ChunkReload] throttled, skipping reload');
          return;
        }

        console.warn('[ChunkReload] navigation chunk error caught, reloading...', msg);
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
      })
    ),
    provideHighcharts(),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideEnvironmentNgxMask(maskConfig),
    importProvidersFrom(
      ToastrModule.forRoot({
        positionClass: 'toast-bottom-right',
        timeOut: 4000,
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      GtagModule.forRoot({ trackingId: 'G-TFYFNYZ1XS' })
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefreshTokenInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: DateInterceptor, multi: true },
    {
      provide: LOCALE_ID,
      useFactory: () => {
        const lang = localStorage.getItem('language') || 'ru';
        const localeMap: Record<string, string> = {
          kz: 'kk',
          ru: 'ru',
          en: 'en-GB',
          ql: 'kk',
        };
        return localeMap[lang] || 'en-GB';
      },
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: ru, // Start with Russian as default
    },

    ...provideDateFnsAdapter(MY_FORMATS),
    {
      provide: 'DATE_RANGE',
      useValue: { min: new Date(1900, 0, 1), max: null },
    },
    MatSnackBar,
  ],
};
